import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getLicence,
  listDocuments,
  listTranslations,
  getTerms,
  getTimeline,
  addTimelineEntry,
  updateLicenceStatus,
  updatePaymentStatus,
  updateLicence,
  archiveLicence,
  requestAdditionalInfo,
  uploadFileAndGetUrl,
  setExpiration,
  markAsReviewed,
  sendBackToReviewer,
  listAdditionalDocuments,
  createDocument,
  lockDocument,
  unlockDocument,
} from "../../services/licences";
import {
  generateInvoice,
  sendInvoice,
  getInvoicePDF,
  syncInvoice,
} from "../../services/invoices";
import {
  listLicenceMessages,
  sendMessage as sendMessageAPI,
  sendInternalMessage,
} from "../../services/messages";
import SendBackToReviewerModal from "../../components/ui/SendBackToReviewerModal";
import DocumentViewer from "../../components/ui/DocumentViewer";
import TranslationRequestsTab from "../../components/licences/TranslationRequestsTab";
import AddSupportDocumentModal from "../../components/ui/AddSupportDocumentModal";
import { listTranslationRequests } from "../../services/translationRequests";
import { listTranslations as listCOATranslations } from "../../services/coa";
import { formatTranslationsRequestedForDisplay } from "../../utils/formatTranslationSelectionLabel";
import {
  ADDITIONAL_INFO_REQUEST_TYPE_OPTIONS,
  DEFAULT_ADDITIONAL_INFO_REQUEST_TYPE,
} from "../../constants/additionalInfoRequestType";

const LicenceDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [openSections, setOpenSections] = useState({
    requestDetails: true,
    supportDocs: true,
    licenceDetails: true,
    orgDetails: true,
    terms: true,
    additionalDocuments: false,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [licenceData, setLicenceData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [coaTranslations, setCoaTranslations] = useState([]);
  const [terms, setTerms] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [messages, setMessages] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [activeChatTab, setActiveChatTab] = useState("applicants");
  const [activeMainTab, setActiveMainTab] = useState(() => {
    // Check URL params for tab
    const tabFromUrl = searchParams.get("tab");
    return tabFromUrl === "translations" ? "translations" : "details";
  }); // 'details' | 'translations'
  const [additionalInfo, setAdditionalInfo] = useState({
    message: "",
    // OLD (kept for revert): requestType: "Upload Document",
    requestType: DEFAULT_ADDITIONAL_INFO_REQUEST_TYPE,
    dueDate: "",
    attachment: null,
  });
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const [pendingPaymentStatus, setPendingPaymentStatus] = useState(null);
  const [expirationDate, setExpirationDate] = useState("");
  const [originalPaymentStatus, setOriginalPaymentStatus] = useState(null);
  const [showSendBackModal, setShowSendBackModal] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRoles, setCurrentUserRoles] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [documentLockToggling, setDocumentLockToggling] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [newTranslationRequestCount, setNewTranslationRequestCount] = useState(0);
  const [translationRequestTotalCount, setTranslationRequestTotalCount] = useState(0);
  const [customerAction, setCustomerAction] = useState({ key: "", value: "" });
  const [savingCustomerAction, setSavingCustomerAction] = useState(false);

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (id) {
      loadData();
      setIsInitialLoad(false);
    }
  }, [id]); // Only reload on id change

  // Separate effect for loading messages when tab changes (skip on initial load)
  useEffect(() => {
    if (id && !isInitialLoad) {
      loadMessages();
    }
  }, [id, activeChatTab]); // Reload messages when tab changes

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load messages based on active tab
      const messageType = activeChatTab === "staff" ? "internal" : "customer";

      const [
        licenceRes,
        docsRes,
        transRes,
        termsRes,
        timelineRes,
        messagesRes,
        additionalDocsRes,
        translationRequestsRes,
      ] = await Promise.all([
        getLicence(id).catch(() => ({ data: null })),
        listDocuments(id).catch(() => ({ data: [] })),
        listTranslations(id).catch(() => ({ data: [] })),
        getTerms(id).catch(() => ({ data: null })),
        getTimeline(id).catch(() => ({ data: [] })),
        listLicenceMessages(id, { type: messageType, limit: 100 }).catch(
          () => ({ data: { messages: [] } }),
        ),
        listAdditionalDocuments(id).catch(() => ({
          success: false,
          data: { documents: [] },
        })),
        listTranslationRequests({ licenceId: id, limit: 100 }).catch(() => ({
          success: false,
          data: { translationRequests: [] },
        })),
      ]);

      if (licenceRes.data) {
        // Ensure customerId is available - it might be populated as an object
        const licence = licenceRes.data;
        // If customerId is populated as an object, ensure we have the ID
        if (
          licence.customerId &&
          typeof licence.customerId === "object" &&
          !licence.customerId._id
        ) {
          // If it's an object but doesn't have _id, it might be the full customer object
          // The backend should handle this, but let's log it for debugging
          console.log("Licence customerId structure:", licence.customerId);
        }
        setLicenceData(licence);
        setCustomerAction({
          key: licence.customerAction?.key ?? "",
          value: licence.customerAction?.value ?? "",
        });
        const coaId = licence.coaId?._id || licence.coaId;
        if (coaId) {
          try {
            const coaTransRes = await listCOATranslations(coaId);
            setCoaTranslations(coaTransRes.data || []);
          } catch {
            setCoaTranslations([]);
          }
        } else {
          setCoaTranslations([]);
        }
        // Load invoice if exists
        if (licenceRes.data.invoiceId) {
          try {
            const { getInvoice } = require("../../services/invoices");
            const invoiceRes = await getInvoice(licenceRes.data.invoiceId);
            if (invoiceRes.success && invoiceRes.data) {
              setInvoice(invoiceRes.data);
            }
          } catch (err) {
            console.error("Failed to load invoice:", err);
          }
        }
      }
      if (docsRes.data) {
        setDocuments(docsRes.data);
      }
      if (transRes.data) {
        setTranslations(transRes.data);
      }
      if (termsRes.data) {
        setTerms(termsRes.data);
      }
      if (timelineRes.data) {
        setTimeline(timelineRes.data);
      }
      if (messagesRes.data && messagesRes.data.messages) {
        setMessages(messagesRes.data.messages);
      }
      if (additionalDocsRes.success && additionalDocsRes.data) {
        setAdditionalDocuments(additionalDocsRes.data.documents || []);
      }

      // Count translation requests awaiting review (new) for tab badge
      if (
        translationRequestsRes &&
        translationRequestsRes.success !== false &&
        translationRequestsRes.data
      ) {
        const list =
          translationRequestsRes.data.translationRequests ||
          translationRequestsRes.data ||
          [];
        const awaitingCount = list.filter((r) => r.status === "Pending").length;
        setNewTranslationRequestCount(awaitingCount);
        setTranslationRequestTotalCount(list.length);
      } else {
        setNewTranslationRequestCount(0);
        setTranslationRequestTotalCount(0);
      }

      // Get current user ID and roles from localStorage
      try {
        // Try both possible localStorage keys
        const userStr =
          localStorage.getItem("auth_user") || localStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          setCurrentUserId(user._id || user.id);
          setCurrentUserRoles(user.roles || []);
        }
      } catch (e) {
        console.error("Failed to get current user:", e);
      }
    } catch (err) {
      console.error("Failed to load licence data:", err);
      setError("Failed to load licence details");
    } finally {
      setLoading(false);
    }
  };

  // Separate function to load only messages and timeline (for modal updates)
  const loadMessages = async () => {
    try {
      const messageType = activeChatTab === "staff" ? "internal" : "customer";

      const [timelineRes, messagesRes] = await Promise.all([
        getTimeline(id).catch(() => ({ data: [] })),
        listLicenceMessages(id, { type: messageType, limit: 100 }).catch(
          () => ({ data: { messages: [] } }),
        ),
      ]);

      if (timelineRes.data) {
        setTimeline(timelineRes.data);
      }
      if (messagesRes.data && messagesRes.data.messages) {
        setMessages(messagesRes.data.messages);
      }
    } catch (err) {
      console.error("Failed to load messages:", err);
      // Don't show error toast for message loading failures in modal
    }
  };

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === licenceData.status) return;

    try {
      setUpdatingStatus(true);
      await updateLicenceStatus(id, newStatus);
      await loadData(); // Reload to get updated data
    } catch (err) {
      console.error("Failed to update status:", err);
      const errorMessage = err?.message || err?.data?.error || "Failed to update status. Please try again.";
      toast.error(errorMessage);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSaveCustomerAction = async () => {
    try {
      setSavingCustomerAction(true);
      await updateLicence(id, {
        customerAction: { key: customerAction.key || "", value: customerAction.value || "" },
      });
      await loadData();
      toast.success("Customer action saved");
    } catch (err) {
      console.error("Failed to save customer action:", err);
      toast.error(err?.message || err?.data?.error || "Failed to save");
    } finally {
      setSavingCustomerAction(false);
    }
  };

  const handlePaymentStatusChange = async (e) => {
    const newStatus = e.target.value;
    const currentStatus = licenceData?.payment?.status || "NotRequired";

    // Validate the new status
    if (!newStatus || newStatus === "null" || newStatus === "undefined") {
      console.error("Invalid payment status value:", newStatus);
      toast.error("Invalid payment status selected");
      // Reset select to current status
      e.target.value = currentStatus;
      return;
    }

    if (newStatus === currentStatus) {
      // Reset select to current status to prevent confusion
      e.target.value = currentStatus;
      return;
    }

    // If setting to Completed and license is Approved, show expiration modal
    if (newStatus === "Completed" && licenceData?.status === "Approved") {
      setPendingPaymentStatus(newStatus);
      setOriginalPaymentStatus(currentStatus);
      // Set default expiration to 30 days from now
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 30);
      setExpirationDate(defaultDate.toISOString().split("T")[0]);
      setShowExpirationModal(true);
      // Keep select value as current status until confirmed
      e.target.value = currentStatus;
      return;
    }

    // Refunded is set by the invoice refund flow only; do not allow changing from Refunded via this form
    if (currentStatus === "Refunded") {
      toast.error("Payment was refunded via the invoice. Status cannot be changed from this page.");
      e.target.value = currentStatus;
      return;
    }

    // For other statuses, update directly (Refunded is display-only, not settable here)
    if (
      !["NotRequired", "Pending", "Completed", "Failed"].includes(newStatus)
    ) {
      console.error("Invalid payment status:", newStatus);
      toast.error("Invalid payment status selected");
      e.target.value = currentStatus;
      return;
    }

    try {
      setUpdatingPayment(true);
      // Ensure we're sending a valid status string
      const statusToSend = String(newStatus).trim();
      if (
        !statusToSend ||
        !["NotRequired", "Pending", "Completed", "Failed"].includes(
          statusToSend,
        )
      ) {
        throw new Error("Invalid status value");
      }
      await updatePaymentStatus(id, statusToSend);
      await loadData(); // Reload to get updated data
    } catch (err) {
      console.error("Failed to update payment status:", err);
      toast.error("Failed to update payment status. Please try again.");
      // Reset select on error
      e.target.value = currentStatus;
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleConfirmExpiration = async () => {
    if (!expirationDate) {
      toast.error("Please select an expiration date");
      return;
    }

    try {
      setUpdatingPayment(true);

      // If pendingPaymentStatus exists, it means we're setting payment to Completed
      // Otherwise, we're just updating the expiration date
      if (pendingPaymentStatus) {
        // First update payment status (this will auto-set expiration if not set)
        await updatePaymentStatus(id, pendingPaymentStatus);
      }

      // Then set custom expiration date (this will override any auto-set expiration)
      await setExpiration(id, expirationDate);

      await loadData(); // Reload to get updated data
      setShowExpirationModal(false);
      setPendingPaymentStatus(null);
      setOriginalPaymentStatus(null);
      setExpirationDate("");
    } catch (err) {
      console.error("Failed to update payment status and expiration:", err);
      toast.error("Failed to update expiration date. Please try again.");
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm("Are you sure you want to archive this licence?")) {
      return;
    }

    try {
      setArchiving(true);
      await archiveLicence(id);
      await loadData(); // Reload to get updated data
      toast.success("Licence archived successfully");
    } catch (err) {
      console.error("Failed to archive licence:", err);
      toast.error("Failed to archive licence. Please try again.");
    } finally {
      setArchiving(false);
    }
  };

  const handleAddSupportDocument = async (newDoc) => {
    if (!id) return;
    try {
      setUploadingDocument(true);
      let fileUrl = newDoc.fileUrl;
      if (newDoc.file && !fileUrl) {
        fileUrl = await uploadFileAndGetUrl(newDoc.file);
      }
      const feeValue =
        newDoc.fee === "£0.00" || !newDoc.fee
          ? 0
          : parseFloat(String(newDoc.fee).replace(/£/g, "").replace(/,/g, "")) || 0;
      await createDocument(id, {
        name: newDoc.name,
        detail: newDoc.detail || "",
        fee: feeValue,
        fileUrl,
        isLocked: newDoc.isLocked !== undefined ? newDoc.isLocked : true,
      });
      toast.success("Support document added");
      setIsAddDocumentModalOpen(false);
      await loadData();
    } catch (err) {
      console.error("Failed to add support document:", err);
      toast.error(err?.message || "Failed to add support document");
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleLockUnlock = async (docId, currentlyLocked) => {
    if (!id || !docId) return;
    try {
      setDocumentLockToggling(docId);
      if (currentlyLocked) {
        await unlockDocument(id, docId);
        toast.success("Document unlocked");
      } else {
        await lockDocument(id, docId);
        toast.success("Document locked");
      }
      await loadData();
    } catch (err) {
      console.error("Lock/unlock failed:", err);
      toast.error(err?.message || "Failed to update lock status");
    } finally {
      setDocumentLockToggling(null);
    }
  };

  const handleMarkAsReviewed = async () => {
    try {
      setUpdatingStatus(true);
      await markAsReviewed(id);
      await loadData(); // Reload to get updated data
      toast.success("Licence marked as reviewed. Approver has been notified.");
    } catch (err) {
      console.error("Failed to mark as reviewed:", err);
      toast.error(
        err.response?.data?.error ||
          "Failed to mark as reviewed. Please try again.",
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSendBackToReviewer = async (data) => {
    try {
      setUpdatingStatus(true);
      await sendBackToReviewer(id, data);
      await loadData(); // Reload to get updated data
      toast.success(
        "Licence sent back to reviewer. Reviewer has been notified.",
      );
      setShowSendBackModal(false);
    } catch (err) {
      console.error("Failed to send back to reviewer:", err);
      toast.error(
        err?.message || "Failed to send back to reviewer. Please try again.",
      );
      throw err; // Re-throw to let modal handle it
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      toast.error("Please enter a message");
      return;
    }

    try {
      setSendingMessage(true);

      // Check if we're in Staff tab - send as internal message
      if (activeChatTab === "staff") {
        // For staff messages, determine recipient (reviewer or approver)
        let toUserId = null;
        
        // Get reviewer and approver IDs from licence data
        const reviewerId = licenceData?.reviewerId?._id || licenceData?.reviewerId?.toString() || licenceData?.reviewerId;
        const approverId = licenceData?.approverId?._id || licenceData?.approverId?.toString() || licenceData?.approverId;
        
        // Get current user ID to determine who to send to
        // If current user is reviewer, send to approver (and vice versa)
        // For SuperAdmin, send to the other staff member or default to reviewer
        const currentUserIdStr = currentUserId?.toString();
        
        if (reviewerId && approverId) {
          // Both exist - determine recipient based on current user
          const reviewerIdStr = reviewerId?.toString();
          const approverIdStr = approverId?.toString();
          
          if (currentUserIdStr && currentUserIdStr === reviewerIdStr) {
            // Current user is reviewer, send to approver
            toUserId = approverId;
          } else if (currentUserIdStr && currentUserIdStr === approverIdStr) {
            // Current user is approver, send to reviewer
            toUserId = reviewerId;
          } else {
            // SuperAdmin or other user - send to reviewer by default
            // (In future, could add UI to select which staff member)
            toUserId = reviewerId;
          }
        } else if (reviewerId) {
          toUserId = reviewerId;
        } else if (approverId) {
          toUserId = approverId;
        }
        // If toUserId is null, message will be visible to all staff
        
        console.log("Sending internal message - activeChatTab:", activeChatTab, "toUserId:", toUserId, "reviewerId:", reviewerId, "approverId:", approverId);
        
        const response = await sendInternalMessage(id, {
          body: messageText.trim(),
          toUserId: toUserId || undefined, // undefined = visible to all staff
        });

        if (!response || !response.success) {
          throw new Error(response?.error || "Failed to send internal message");
        }
      } else {
        // Send customer message (Applicants tab)
        // Get customerId - handle both string and object formats
        let customerId;
        if (licenceData?.customerId) {
          if (
            typeof licenceData.customerId === "object" &&
            licenceData.customerId !== null
          ) {
            customerId =
              licenceData.customerId._id ||
              licenceData.customerId.id ||
              licenceData.customerId.toString();
          } else {
            customerId = licenceData.customerId.toString();
          }
        }

        if (!customerId) {
          console.error("Customer ID is missing. Licence data:", licenceData);
          toast.error(
            "Customer information not available. Please refresh the page and try again.",
          );
          setSendingMessage(false);
          return;
        }

        console.log("Sending customer message with customerId:", customerId);

        // Send message to customer using messaging API
        const response = await sendMessageAPI(id, {
          body: messageText.trim(),
          toCustomerId: customerId,
          isInternal: false,
          messageType: "text",
        });

        if (!response || !response.success) {
          throw new Error(response?.error || "Failed to send message");
        }
      }

      // Don't add timeline entry - messages are already displayed in the messages section
      // Timeline entries are for system events, not user messages

      setMessageText("");
      await loadMessages(); // Only reload messages, not the entire page
    } catch (err) {
      console.error("Failed to send message:", err);
      const errorMessage =
        err?.data?.error ||
        err?.message ||
        err?.error ||
        "Failed to send message. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return null;

    try {
      setUploadingFile(true);
      const fileUrl = await uploadFileAndGetUrl(file);
      return fileUrl;
    } catch (error) {
      console.error("File upload failed:", error);
      toast.error("Failed to upload file. Please try again.");
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSendAdditionalInfoRequest = async () => {
    if (!additionalInfo.message.trim()) {
      toast.error("Please enter a message to the applicant");
      return;
    }

    // Get customerId - handle both string and object formats
    let customerId;
    if (licenceData?.customerId) {
      if (
        typeof licenceData.customerId === "object" &&
        licenceData.customerId !== null
      ) {
        // Handle populated customer object
        customerId = licenceData.customerId._id || licenceData.customerId.id;
        if (!customerId && licenceData.customerId.toString) {
          // If _id is not available, try toString (for ObjectId)
          const idStr = licenceData.customerId.toString();
          if (idStr && idStr !== "[object Object]") {
            customerId = idStr;
          }
        }
      } else {
        // Handle string or ObjectId
        customerId = licenceData.customerId.toString();
      }
    }

    if (!customerId) {
      console.error("Customer ID is missing. Licence data:", {
        licenceId: licenceData?._id || licenceData?.id,
        customerId: licenceData?.customerId,
        customerIdType: typeof licenceData?.customerId,
        fullLicenceData: licenceData,
      });
      toast.error(
        "Customer information not available. This licence may not have an associated customer. Please contact support.",
      );
      return;
    }

    try {
      setSendingRequest(true);

      // Upload attachment if provided
      let attachmentUrl = null;
      let attachmentFilename = null;

      if (additionalInfo.attachment) {
        const uploadedUrl = await handleFileUpload(additionalInfo.attachment);
        if (uploadedUrl) {
          attachmentUrl = uploadedUrl;
          attachmentFilename = additionalInfo.attachment.name;
        } else {
          toast.error("File upload failed. Please try again.");
          setSendingRequest(false);
          return;
        }
      }

      // Prepare payload
      const payload = {
        message: additionalInfo.message.trim(),
        requestType: additionalInfo.requestType,
        dueDate: additionalInfo.dueDate || undefined,
        attachmentUrl: attachmentUrl || undefined,
        attachmentFilename: attachmentFilename || undefined,
      };

      // Send additional info request
      const response = await requestAdditionalInfo(id, payload);

      if (!response || !response.success) {
        throw new Error(response?.error || "Failed to send request");
      }

      // Reset form
      setAdditionalInfo({
        message: "",
        // OLD (kept for revert): requestType: "Upload Document",
        requestType: DEFAULT_ADDITIONAL_INFO_REQUEST_TYPE,
        dueDate: "",
        attachment: null,
      });

      // Reload data to show new message
      await loadData();

      toast.success("Additional information request sent successfully");
    } catch (err) {
      console.error("Failed to send additional info request:", err);
      const errorMessage =
        err?.data?.error ||
        err?.message ||
        err?.error ||
        "Failed to send request. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSendingRequest(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
  };

  const handleFileDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setAdditionalInfo({ ...additionalInfo, attachment: file });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAdditionalInfo({ ...additionalInfo, attachment: file });
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading...</div>;
  }

  if (error || !licenceData) {
    return (
      <div className="p-8 text-center text-red-500">
        {error || "Licence not found"}
      </div>
    );
  }

  const formatNoteDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const notes = Array.isArray(licenceData.notes) ? licenceData.notes : [];

  // Format data for display
  const requestedTranslationCount = Array.isArray(licenceData?.translationsRequested)
    ? licenceData.translationsRequested.length
    : 0;
  // Prefer requested count (what customer selected) over created Translation docs (which can be 0 pre-processing)
  const translationCount =
    requestedTranslationCount > 0
      ? requestedTranslationCount
      : licenceData?.customTranslationsText
        ? 1
        : translations.length;

  const formattedData = {
    requestNumber: licenceData.refNumber || "N/A",
    submittedDate: licenceData.submittedAt
      ? new Date(licenceData.submittedAt).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "N/A",
    status: licenceData.status || "N/A",
    fee: licenceData.fees?.licenceFee
      ? `£${licenceData.fees.licenceFee.toFixed(2)}`
      : "£0.00",
    translationFee: licenceData.fees?.translationFeeTotal
      ? `£${licenceData.fees.translationFeeTotal.toFixed(2)}`
      : "£0.00",
    supportFee: licenceData.fees?.supportDocsFeeTotal
      ? `£${licenceData.fees.supportDocsFeeTotal.toFixed(2)}`
      : "£0.00",
    ecoaFee: licenceData.fees?.ecoaFeeTotal
      ? `£${licenceData.fees.ecoaFeeTotal.toFixed(2)}`
      : "£0.00",
    totalPrice: licenceData.fees?.totalPrice
      ? `£${licenceData.fees.totalPrice.toFixed(2)}`
      : "£0.00",
    paymentStatus: licenceData.payment?.status || "NotRequired",
    coaName: licenceData.coaId?.name || "N/A",
    organizationType: licenceData.organizationType || "N/A",
    intendedUse: licenceData.intendedUse || "N/A",
    numberTranslations:
      translations.map((t) => `${t.country} - ${t.language}`).join(", ") ||
      "N/A",
    translationsRequested: (() => {
      const requested = licenceData.translationsRequested;
      if (!Array.isArray(requested) || requested.length === 0) return "None";
      const formatted = formatTranslationsRequestedForDisplay(requested, coaTranslations);
      return formatted || "None";
    })(),
    customTranslations: licenceData.customTranslationsText || "N/A",
    translationTerritory: licenceData.translationTerritory || "N/A",
    wantOriginalTranslation: licenceData.wantOriginalTranslation ? "Yes" : "No",
    supportDocumentsSelected: (() => {
      // Check if supportDocumentsSelected exists (new field with document details)
      if (
        Array.isArray(licenceData.supportDocumentsSelected) &&
        licenceData.supportDocumentsSelected.length > 0
      ) {
        return licenceData.supportDocumentsSelected
          .map((doc) => doc.name || "Document")
          .join(", ");
      }
      // Fallback: check if supportDocuments is an array of IDs or objects
      if (
        Array.isArray(licenceData.supportDocuments) &&
        licenceData.supportDocuments.length > 0
      ) {
        // If it's an array of objects with names
        if (
          typeof licenceData.supportDocuments[0] === "object" &&
          licenceData.supportDocuments[0].name
        ) {
          return licenceData.supportDocuments.map((doc) => doc.name).join(", ");
        }
        // If it's an array of IDs, try to match with documents
        const selectedDocIds = licenceData.supportDocuments.map((id) =>
          id.toString(),
        );
        const selectedDocs = documents.filter((doc) =>
          selectedDocIds.includes(doc._id?.toString() || doc.id?.toString()),
        );
        if (selectedDocs.length > 0) {
          return selectedDocs.map((doc) => doc.name || "Document").join(", ");
        }
        // If no matches, show the IDs
        return licenceData.supportDocuments.join(", ");
      }
      return "None";
    })(),
    territory: Array.isArray(licenceData.territory)
      ? licenceData.territory.join(", ")
      : licenceData.territory || "N/A",
    eCOA: licenceData.eCOA ? "Yes" : "No",
    startDate: licenceData.startDate
      ? new Date(licenceData.startDate).toISOString().split("T")[0]
      : "N/A",
    endDate: licenceData.endDate
      ? new Date(licenceData.endDate).toISOString().split("T")[0]
      : "N/A",
    users: licenceData.numUsers?.toString() || "N/A",
    usesNumber: licenceData.numUses?.toString() || "N/A",
    pqTrustNumber: licenceData.pqTrustNumber || "N/A",
    fullName: licenceData.contact?.fullName || "N/A",
    jobTitle: licenceData.contact?.jobTitle || "N/A",
    email: licenceData.contact?.email || "N/A",
    phone: licenceData.contact?.phone || "N/A",
    orgName: licenceData.org?.name || "N/A",
    address1: licenceData.org?.address1 || "N/A",
    address2: licenceData.org?.address2 || "",
    city: licenceData.org?.city || "N/A",
    region: licenceData.org?.region || "N/A",
    postcode: licenceData.org?.postcode || "N/A",
    country: licenceData.org?.country || "N/A",
    vatNumber: licenceData.org?.vatNumber || "N/A",
  };

  return (
    <>
      <div className="bg-[#fafafa] min-h-screen p-6">
        <div className="max-w-[1920px] mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/requested-licences")}
                className="px-4 py-2 h-[40px] border border-[#e7ebe9] bg-white text-[#485b54] rounded-[8px] text-[14px] font-medium hover:bg-[#F8F9FA] transition-colors flex items-center gap-2"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 12L6 8L10 4" />
                </svg>
                Back
              </button>
              <h1 className="text-[32px] font-semibold text-[#212529] tracking-[-0.64px]">
                View Licence Details
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/edit-license-request/${id}`)}
                className="px-6 py-2 h-[40px] border border-[#e7ebe9] bg-white text-[#485b54] rounded-[8px] text-[14px] font-medium hover:bg-[#F8F9FA] transition-colors"
              >
                Edit
              </button>
              <button
                onClick={handleArchive}
                disabled={archiving || licenceData.status === "Archived"}
                className="px-6 py-2 h-[40px] border border-[#e7ebe9] bg-white text-[#485b54] rounded-[8px] text-[14px] font-medium hover:bg-[#F8F9FA] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {archiving ? "Archiving..." : "Archived"}
              </button>
              {/* Mark as Reviewed button - only show if user is reviewer and status is Under Review, Re-review, or Under Review – Additional Info Required */}
              {licenceData.reviewerId &&
                currentUserId &&
                licenceData.reviewerId._id?.toString() === currentUserId &&
                (licenceData.status === "Under Review" ||
                  licenceData.status === "Re-review" ||
                  licenceData.status ===
                    "Under Review – Additional Info Required") && (
                  <button
                    onClick={handleMarkAsReviewed}
                    disabled={updatingStatus}
                    className="px-6 py-2 h-[40px] bg-gradient-to-r from-[#40C191] to-[#5ED58D] text-white rounded-[8px] text-[14px] font-medium hover:from-[#35A47C] hover:to-[#4EC57A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updatingStatus ? "Updating..." : "Mark as Reviewed"}
                  </button>
                )}
              {/* Send Back to Reviewer button - show if user is approver (by role or assignment) and status is Reviewed or Approved */}
              {currentUserId &&
                (licenceData.status === "Reviewed" ||
                  licenceData.status === "Approved") &&
                (currentUserRoles.some((r) => (r && String(r).toLowerCase()) === "approver") ||
                  currentUserRoles.includes("SuperAdmin") ||
                  (licenceData.approverId && (licenceData.approverId._id?.toString() || licenceData.approverId?.toString()) === (currentUserId?.toString?.() || String(currentUserId)))) && (
                  <button
                    onClick={() => setShowSendBackModal(true)}
                    disabled={updatingStatus}
                    className="px-6 py-2 h-[40px] bg-gradient-to-r from-[#FF6B6B] to-[#FF8E8E] text-white rounded-[8px] text-[14px] font-medium hover:from-[#E55555] hover:to-[#FF7A7A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send Back to Reviewer
                  </button>
                )}
            </div>
          </div>

          {/* Main Tabs */}
          <div className="bg-white border border-[#f2f2f2] rounded-[16px] mb-[16px]">
            <div className="bg-[#f2f2f2] p-[6px] border-b border-[#f2f2f2]">
              <div className="flex gap-0">
                {["details", "translations"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveMainTab(tab)}
                    className={`flex-1 px-[16px] py-[6px] text-[14px] font-medium rounded-[8px] transition-colors flex items-center justify-center gap-2 ${
                      activeMainTab === tab
                        ? "bg-emerald-400 text-white"
                        : "text-emerald-400 hover:bg-white"
                    }`}
                  >
                    {tab === "details" ? "Details" : `Translations (${translationRequestTotalCount})`}
                    {tab === "translations" && newTranslationRequestCount > 0 && (
                      <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-semibold rounded-full">
                        {newTranslationRequestCount > 99
                          ? "99+"
                          : newTranslationRequestCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {activeMainTab === "details" ? (
          <div className="flex gap-[16px] items-start">
            {/* Left Column - Main Content */}
            <div className="flex-1 space-y-[16px]">
              {/* Change Note Alert - Only show for reviewer when note exists */}
              {licenceData.changeNote &&
                licenceData.reviewerId &&
                currentUserId &&
                licenceData.reviewerId._id?.toString() === currentUserId &&
                (licenceData.status === "Under Review" ||
                  licenceData.status === "Re-review" ||
                  licenceData.status === "Awaiting Review" ||
                  licenceData.status ===
                    "Under Review – Additional Info Required") && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-red-500"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-red-800">
                          Request Sent Back for Review
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p className="font-medium mb-2">
                            Note from Approver:
                          </p>
                          <p className="whitespace-pre-wrap bg-red-100 p-3 rounded border-2 border-red-400">
                            {licenceData.changeNote}
                          </p>
                          {licenceData.changeNoteAddedAt && (
                            <p className="mt-2 text-xs text-red-600">
                              Added on{" "}
                              {new Date(
                                licenceData.changeNoteAddedAt,
                              ).toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/* Licence Request Details */}
              <div className="bg-white border border-[#f2f2f2] rounded-[16px] p-[16px]">
                {/* BoxTitle */}
                <div className="flex items-center gap-[12px] mb-[8px]">
                  <div className="bg-[#f0fff2] w-[34px] h-[34px] rounded-[8px] flex items-center justify-center">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="#41c291"
                      strokeWidth="1.5"
                    >
                      <path
                        d="M16.6667 5.83333H3.33333C2.8731 5.83333 2.5 6.20643 2.5 6.66667V15C2.5 15.4602 2.8731 15.8333 3.33333 15.8333H16.6667C17.1269 15.8333 17.5 15.4602 17.5 15V6.66667C17.5 6.20643 17.1269 5.83333 16.6667 5.83333Z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13.3333 5.83333V4.16667C13.3333 3.72464 13.1577 3.30072 12.8452 2.98816C12.5326 2.67559 12.1087 2.5 11.6667 2.5H8.33333C7.89131 2.5 7.46738 2.67559 7.15482 2.98816C6.84226 3.30072 6.66667 3.72464 6.66667 4.16667V5.83333"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[18px] font-medium text-[#222f2a]">
                      View Licence Details:
                    </h2>
                    <p className="text-[14px] text-[#73827c] mt-[2px]">
                      (Licence Request Details)
                    </p>
                  </div>
                </div>

                {/* Content List Items */}
                <div className="space-y-0">
                  {/* COA Name */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        COA Name:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (Name of the Clinical Outcome Assessment instrument)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.coaName}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {/* Organisation Type */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        Organisation Type:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (Type of organisation submitting the request, e.g.,
                        sponsor, CRO, academic)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.organizationType}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {/* Intended Use */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        Intended Use:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (How the applicant intends to use the questionnaire in
                        their research/study)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.intendedUse === "N/A"
                        ? ""
                        : formattedData.intendedUse}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {/* Requested Translations */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        Requested Translations:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (Customer has selected a translation from our COA
                        library or one currently in development)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.translationsRequested}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {/* Available Translations */}
                  {/* <div className="flex gap-[16px] items-start py-[12px]">
                  <div className="flex-1 flex items-center gap-[8px]">
                    <p className="font-medium text-[16px] text-[#485b54]">Available Translations:</p>
                    <div className="bg-[#f0fff2] flex items-center gap-[8px] px-[8px] py-[5px] rounded-full">
                      <p className="text-[12px] text-[#41c291]">{translations.length} Available</p>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#41c291" strokeWidth="1.5">
                        <path d="M8 13.3333C11.3137 13.3333 14 10.647 14 7.33333C14 4.01962 11.3137 1.33333 8 1.33333C4.68629 1.33333 2 4.01962 2 7.33333C2 10.647 4.68629 13.3333 8 13.3333Z" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 5.33333V8" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 10.6667H8.00667" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">{formattedData.numberTranslations}</p>
                </div>
                <div className="h-px bg-[#ebebeb]"></div> */}

                  {/* Custom Translations */}
                  {formattedData.customTranslations !== "N/A" && (
                    <>
                      <div className="flex gap-[16px] items-start py-[12px]">
                        <div className="flex-1">
                          <p className="font-medium text-[16px] text-[#485b54]">
                            Custom New Translation:
                          </p>
                          <p className="text-[12px] text-[#73827c] mt-[4px]">
                            (Translations the customer wants to develop,
                            requests permission to develop themselves, or asks
                            us to commission)
                          </p>
                        </div>
                        <div className="flex-1 flex items-center border border-[#40C191] rounded-[8px] p-[8px] gap-[16px] justify-end">
                          <p className="font-medium text-[14px] text-[#73827c]">
                            {formattedData.customTranslations}
                          </p>
                          {/* <div className="bg-[rgba(64,199,60,0.05)] border border-[#40c73c] flex items-center gap-[8px] px-[8px] py-[6px] rounded-[8px]">
                          <div className="w-[7px] h-[7px] rounded-full bg-[#40c73c]"></div>
                          <p className="text-[12px] text-[#40c73c]">Approved</p>
                        </div> */}
                        </div>
                      </div>
                      <div className="h-px bg-[#ebebeb]"></div>
                    </>
                  )}

                  {/* Translation Territory */}
                  {formattedData.translationTerritory !== "N/A" && (
                    <>
                      <div className="flex gap-[16px] items-start py-[12px]">
                        <div className="flex-1">
                          <p className="font-medium text-[16px] text-[#485b54]">
                            Translation Territory:
                          </p>
                          <p className="text-[12px] text-[#73827c] mt-[4px]">
                            (The territory where the translation will be used)
                          </p>
                        </div>
                        <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                          {formattedData.translationTerritory}
                        </p>
                      </div>
                      <div className="h-px bg-[#ebebeb]"></div>
                    </>
                  )}

                  {/* Want Original Translation */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        Original Source Version Requested:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (Has the customer requested the original developer
                        questionnaire in its original language?)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.wantOriginalTranslation}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {/* Territories */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        Territories / Regions:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (All geographic regions where the COA will be deployed)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.territory}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {/* eCOA */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        eCOA Migration Requested:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (Has the customer asked for permission to digitally
                        migrate the instrument?)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.eCOA}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {/* Start Date */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        Start Date:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (Planned start date for use)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.startDate}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {/* End Date */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        End Date:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (Planned end date for use)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.endDate}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {/* Number of Users */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        Number of Users:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (Total number of users who will access the instrument)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.users}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {/* Number of Uses */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        Number of Uses:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (Estimated number of times the instrument will be used)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.usesNumber}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {/* PQTrust Number */}
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <div className="flex-1">
                      <p className="font-medium text-[16px] text-[#485b54]">
                        PO / Trial Number:
                      </p>
                      <p className="text-[12px] text-[#73827c] mt-[4px]">
                        (Purchase Order or Trial Identifier)
                      </p>
                    </div>
                    <p className="flex-1 font-medium text-[14px] text-[#73827c] text-right">
                      {formattedData.pqTrustNumber}
                    </p>
                  </div>
                </div>
              </div>

              {/* Support Documents */}
              <div className="bg-white border border-[#f2f2f2] rounded-[16px] p-[16px]">
                {/* BoxTitle */}
                <div className="flex items-center gap-[12px] mb-[12px]">
                  <div className="bg-[#f0fff2] w-[34px] h-[34px] rounded-[8px] flex items-center justify-center">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="#41c291"
                      strokeWidth="1.5"
                    >
                      <path
                        d="M11.6667 1.66667H5C4.55797 1.66667 4.13405 1.84226 3.82149 2.15482C3.50893 2.46738 3.33334 2.8913 3.33334 3.33333V16.6667C3.33334 17.1087 3.50893 17.5326 3.82149 17.8452C4.13405 18.1577 4.55797 18.3333 5 18.3333H15C15.442 18.3333 15.866 18.1577 16.1785 17.8452C16.4911 17.5326 16.6667 17.1087 16.6667 16.6667V6.66667L11.6667 1.66667Z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M11.6667 1.66667V6.66667H16.6667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h2 className="text-[18px] font-medium text-[#222f2a]">
                    Support Documents
                  </h2>
                </div>

                {/* Document Cards - minimal compact design (like customer app) */}
                <div className="flex flex-wrap gap-2">
                  {(!licenceData?.supportDocumentsSelected || licenceData.supportDocumentsSelected.length === 0) ? (
                    <p className="text-[14px] text-[#73827c] py-4">
                      No support documents selected
                    </p>
                  ) : (
                    licenceData.supportDocumentsSelected.map((doc) => {
                      const detail = doc.detail || "No description";
                      const detailShort = detail.length > 20 ? detail.slice(0, 20) + "…" : detail;
                      return (
                        <div
                          key={doc._id || doc.name}
                          className="w-48 flex-none bg-[#fafafa] border border-[#ebebeb] rounded-lg p-2 flex items-start gap-2"
                        >
                          <div className="w-8 h-8 shrink-0 bg-[#EBF9F3] rounded flex items-center justify-center">
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#40C191" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z" />
                              <path d="M14 2v4h4" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-[#485b54] truncate" title={doc.name}>{doc.name || "Document"}</p>
                            <p className="text-xs text-[#73827c] truncate" title={detail}>{detailShort}</p>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              <span className="text-xs text-[#485b54]">
                                {(Number(doc.fee) || 0) > 0 ? `£${typeof doc.fee === "number" ? doc.fee.toFixed(2) : doc.fee}` : "Free"}
                              </span>
                              {doc.locked ? (
                                <span className="text-xs text-amber-600 font-medium">Locked</span>
                              ) : (
                                <span className="text-xs text-[#73827c]">Unlocked</span>
                              )}
                              <button
                                type="button"
                                disabled={documentLockToggling === doc._id}
                                onClick={() => handleLockUnlock(doc._id, doc.locked)}
                                className="text-xs px-2 py-0.5 font-medium rounded border border-[#E5E7EB] bg-white text-[#485b54] hover:bg-[#F8F9FA] disabled:opacity-50"
                              >
                                {documentLockToggling === doc._id ? "…" : doc.locked ? "Unlock" : "Lock"}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setIsAddDocumentModalOpen(true)}
                    disabled={uploadingDocument}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#40C191] hover:bg-[#35A47C] rounded-lg disabled:opacity-50"
                  >
                    {uploadingDocument ? "Uploading…" : "Upload support document"}
                  </button>
                </div>
              </div>

              {/* Licence Details */}
              <div className="bg-white border border-[#f2f2f2] rounded-[16px] p-[16px]">
                {/* BoxTitle */}
                <div className="flex items-center gap-[12px] mb-[8px]">
                  <div className="bg-[#f0fff2] w-[34px] h-[34px] rounded-[8px] flex items-center justify-center">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="#41c291"
                      strokeWidth="1.5"
                    >
                      <path
                        d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2.5 18.3333C2.5 15.5719 5.23858 13.3333 8.5 13.3333H11.5C14.7614 13.3333 17.5 15.5719 17.5 18.3333"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h2 className="text-[18px] font-medium text-[#222f2a]">
                    Licence Details
                  </h2>
                </div>

                {/* Content List Items */}
                <div className="space-y-0">
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Full Name:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.fullName}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Job Title:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.jobTitle}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Email:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.email}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Phone:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.phone}
                    </p>
                  </div>
                </div>
              </div>

              {/* Organisation Details */}
              <div className="bg-white border border-[#f2f2f2] rounded-[16px] p-[16px]">
                {/* BoxTitle */}
                <div className="flex items-center gap-[12px] mb-[8px]">
                  <div className="bg-[#f0fff2] w-[34px] h-[34px] rounded-[8px] flex items-center justify-center">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="#41c291"
                      strokeWidth="1.5"
                    >
                      <path
                        d="M16.6667 2.5H3.33333C2.8731 2.5 2.5 2.8731 2.5 3.33333V16.6667C2.5 17.1269 2.8731 17.5 3.33333 17.5H16.6667C17.1269 17.5 17.5 17.1269 17.5 16.6667V3.33333C17.5 2.8731 17.1269 2.5 16.6667 2.5Z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6.66667 7.5H13.3333"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6.66667 10.8333H13.3333"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6.66667 14.1667H10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h2 className="text-[18px] font-medium text-[#222f2a]">
                    Organisation Details
                  </h2>
                </div>

                {/* Content List Items */}
                <div className="space-y-0">
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Organisation Name:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.orgName}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Address 1:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.address1}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  {formattedData.address2 &&
                    formattedData.address2 !== "N/A" && (
                      <>
                        <div className="flex gap-[16px] items-start py-[12px]">
                          <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                            Address 2:
                          </p>
                          <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                            {formattedData.address2}
                          </p>
                        </div>
                        <div className="h-px bg-[#ebebeb]"></div>
                      </>
                    )}

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      City:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.city}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Region:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.region}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Postcode:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.postcode}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Country:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.country}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      VAT Number:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.vatNumber}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-white border border-[#f2f2f2] rounded-[16px] p-[16px] mt-[16px]">
                <div className="flex items-center gap-[12px] mb-[8px]">
                  <div className="bg-[#f0fff2] w-[34px] h-[34px] rounded-[8px] flex items-center justify-center">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="#41c291"
                      strokeWidth="1.5"
                    >
                      <path
                        d="M10 2.5H5C3.61929 2.5 2.5 3.61929 2.5 5V15C2.5 16.3807 3.61929 17.5 5 17.5H15C16.3807 17.5 17.5 16.3807 17.5 15V10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12.5 4.16667L15.8333 7.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8.33334 11.6667H12.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8.33334 8.33333H10.8333"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h2 className="text-[18px] font-medium text-[#222f2a]">Notes</h2>
                </div>

                <div className="space-y-4">
                  {notes.length === 0 ? (
                    <p className="text-sm text-[#73827c] text-center py-4">
                      No notes yet
                    </p>
                  ) : (
                    notes
                      .slice()
                      .reverse()
                      .map((note, index) => (
                        <div
                          key={index}
                          className="border border-[#E5E7EB] rounded-lg p-4 bg-[#fafafa]"
                        >
                          <p className="text-sm text-[#485B54] mb-2 whitespace-pre-wrap">
                            {note.note}
                          </p>
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-semibold text-[#222f2a]">
                              {note.addedBy?.fullName || "Unknown User"}
                            </span>
                            <span className="font-semibold text-[#485b54]">
                              {formatNoteDate(note.addedAt)}
                            </span>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="w-[320px] space-y-[16px] flex-shrink-0">
              {/* Licence Summary */}
              <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
                <div className="space-y-0">
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Licence ID
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.requestNumber}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Submitted:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.submittedDate}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-center py-[12px]">
                    <p className="font-medium text-[16px] text-[#485b54]">
                      Status:
                    </p>
                    <div className="flex-1 relative">
                      <select
                        value={formattedData.status}
                        onChange={handleStatusChange}
                        disabled={updatingStatus}
                        className="w-full px-[12px] py-[8px] bg-white border border-[#ebebeb] rounded-[8px] text-[14px] text-[#73827c] appearance-none focus:outline-none focus:ring-2 focus:ring-[#40C191] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="Awaiting Review">Awaiting Review</option>
                        <option value="Under Review – Additional Info Required">
                          Under Review – Additional Info Required
                        </option>
                        <option value="Under Review">Under Review</option>
                        <option value="Re-review">Re-review</option>
                        <option value="Reviewed">Reviewed</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Archived">Archived</option>
                      </select>
                      <div className="absolute right-[12px] top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          stroke="#73827c"
                          strokeWidth="1.5"
                        >
                          <path
                            d="M5 7.5L10 12.5L15 7.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-center py-[12px] flex-wrap">
                    <p className="font-medium text-[16px] text-[#485b54] w-full">
                      Customer Action:
                    </p>
                    <div className="flex-1 min-w-[140px]">
                      <input
                        type="text"
                        placeholder="Status for table"
                        value={customerAction.key}
                        onChange={(e) => setCustomerAction((prev) => ({ ...prev, key: e.target.value }))}
                        className="w-full px-[12px] py-[8px] bg-white border border-[#ebebeb] rounded-[8px] text-[14px] text-[#485b54] placeholder:text-[#73827c] focus:outline-none focus:ring-2 focus:ring-[#40C191]"
                      />
                    </div>
                    <div className="flex-1 min-w-[140px]">
                      <input
                        type="text"
                        placeholder="Details..."
                        value={customerAction.value}
                        onChange={(e) => setCustomerAction((prev) => ({ ...prev, value: e.target.value }))}
                        className="w-full px-[12px] py-[8px] bg-white border border-[#ebebeb] rounded-[8px] text-[14px] text-[#485b54] placeholder:text-[#73827c] focus:outline-none focus:ring-2 focus:ring-[#40C191]"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSaveCustomerAction}
                      disabled={savingCustomerAction}
                      className="px-4 py-2 h-[36px] bg-[#40C191] hover:bg-[#35A47C] text-white rounded-[8px] text-[14px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingCustomerAction ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Additional Documents Accordion */}
              {additionalDocuments.length > 0 && (
                <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
                  <button
                    onClick={() =>
                      setOpenSections((prev) => ({
                        ...prev,
                        additionalDocuments: !prev.additionalDocuments,
                      }))
                    }
                    className="w-full flex items-center justify-between mb-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-[#f0fff2] flex items-center justify-center p-2 rounded-[8px]">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          stroke="#41c291"
                          strokeWidth="1.5"
                        >
                          <path
                            d="M4.16667 5.83333H15.8333"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4.16667 10H15.8333"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4.16667 14.1667H15.8333"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <h3 className="text-[18px] font-normal text-[#222f2a]">
                        Additional Documents ({additionalDocuments.length})
                      </h3>
                    </div>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="#73827C"
                      strokeWidth="1.5"
                      className={`transition-transform ${openSections.additionalDocuments ? "rotate-180" : ""}`}
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {openSections.additionalDocuments && (
                    <div className="space-y-3">
                      {additionalDocuments.map((doc, index) => (
                        <div
                          key={doc.id || index}
                          className="flex gap-4 items-center justify-between px-4 py-3 bg-[#F8F9FA] rounded-[8px] hover:bg-[#EBF9F3] transition-colors"
                        >
                          <div className="flex-1 flex items-center gap-3">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#40C191"
                              strokeWidth="1.5"
                            >
                              <path
                                d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M14 2V8H20"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <div className="flex-1">
                              <p className="font-medium text-[14px] text-[#485B54]">
                                {doc.name}
                              </p>
                              <p className="text-[12px] text-[#73827C] mt-1">
                                Uploaded:{" "}
                                {new Date(doc.uploadedAt).toLocaleDateString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )}
                                {doc.uploadedBy && ` by ${doc.uploadedBy.name}`}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedDocument({
                                name: doc.name,
                                url: doc.url,
                                type: doc.mimeType || "application/pdf",
                              });
                              setShowDocumentViewer(true);
                            }}
                            className="px-4 py-2 bg-[#40C191] hover:bg-[#35A47C] text-white rounded-[8px] text-[12px] font-medium transition-colors"
                          >
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Open Message Box Button */}
              <button
                onClick={() => setShowMessageModal(true)}
                className="w-full px-4 py-3 bg-[#40C191] hover:bg-[#35A47C] text-white rounded-[12px] text-[14px] font-medium transition-colors flex items-center justify-center gap-2"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path
                    d="M17.5 9.58333C17.5029 10.6832 17.2459 11.7682 16.75 12.75C16.162 13.9265 15.2581 14.916 14.1395 15.6077C13.021 16.2995 11.7319 16.6662 10.4167 16.6667C9.31678 16.6695 8.23176 16.4126 7.25 15.9167L2.5 17.5L4.08333 12.75C3.58744 11.7682 3.33047 10.6832 3.33333 9.58333C3.33384 8.26813 3.70051 6.97904 4.39227 5.86045C5.08402 4.74187 6.07355 3.83797 7.25 3.25C8.23176 2.75411 9.31678 2.49713 10.4167 2.5H10.8333C12.5703 2.59583 14.2109 3.32899 15.4409 4.55907C16.671 5.78915 17.4042 7.42973 17.5 9.16667V9.58333Z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Open Message Box
              </button>

              {/* Additional Info Required */}
              <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
                <h3 className="text-[18px] font-normal text-[#222f2a] mb-[24px] pb-[8px]">
                  Additional Info Required
                </h3>

                <div className="space-y-[12px]">
                  <div>
                    <label className="block text-[14px] text-[#485b54] mb-[4px]">
                      Message to Applicant
                    </label>
                    <textarea
                      value={additionalInfo.message}
                      onChange={(e) =>
                        setAdditionalInfo({
                          ...additionalInfo,
                          message: e.target.value,
                        })
                      }
                      className="w-full h-[96px] px-[12px] py-[12px] bg-white border border-[#f2f2f2] rounded-[8px] text-[14px] text-[#485b54] focus:outline-none focus:ring-2 focus:ring-[#40C191] resize-none"
                      placeholder="Enter message..."
                    />
                  </div>

                  <div>
                    <label className="block text-[14px] text-[#485b54] mb-[4px]">
                      Request Type
                    </label>
                    <div className="relative">
                      <select
                        value={additionalInfo.requestType}
                        onChange={(e) =>
                          setAdditionalInfo({
                            ...additionalInfo,
                            requestType: e.target.value,
                          })
                        }
                        className="w-full px-[12px] py-[8px] bg-white border border-[#ebebeb] rounded-[8px] text-[14px] text-[#73827c] appearance-none focus:outline-none focus:ring-2 focus:ring-[#40C191]"
                      >
                        {/* OLD (kept for revert):
                        <option value="Upload Document">Upload Document</option>
                        <option value="Provide Information">
                          Provide Information
                        </option>
                        <option value="Clarification">Clarification</option>
                        */}
                        {ADDITIONAL_INFO_REQUEST_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-[12px] top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          stroke="#73827c"
                          strokeWidth="1.5"
                        >
                          <path
                            d="M5 7.5L10 12.5L15 7.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[14px] text-[#485b54] mb-[4px]">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={additionalInfo.dueDate}
                      onChange={(e) =>
                        setAdditionalInfo({
                          ...additionalInfo,
                          dueDate: e.target.value,
                        })
                      }
                      className="w-full px-[12px] py-[8px] bg-white border border-[#f2f2f2] rounded-[8px] text-[14px] text-[#73827c] focus:outline-none focus:ring-2 focus:ring-[#40C191]"
                    />
                  </div>

                  <div>
                    <label className="block text-[14px] text-[#485b54] mb-[4px]">
                      Attach Reference/Guide
                    </label>
                    <div
                      className={`border-2 border-dashed rounded-[8px] h-[57px] flex items-center justify-center cursor-pointer transition-colors relative ${
                        isDraggingFile
                          ? 'border-[#40c191] bg-[#f0fff2]'
                          : 'border-[#41c291] hover:bg-[#f0fff2]'
                      }`}
                      onDragEnter={handleDragEnter}
                      onDragOver={(e) => e.preventDefault()}
                      onDragLeave={handleDragLeave}
                      onDrop={handleFileDrop}
                      onClick={() =>
                        document.getElementById("file-upload-input")?.click()
                      }
                    >
                      <input
                        id="file-upload-input"
                        type="file"
                        className="hidden"
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.txt"
                      />
                      <div className="flex items-center gap-[8px]">
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 22 22"
                          fill="none"
                          stroke="#41c291"
                          strokeWidth="1.5"
                        >
                          <path
                            d="M11 1.83333V14.6667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7.33333 5.5L11 1.83333L14.6667 5.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M3.66667 14.6667V18.3333C3.66667 19.2538 4.41286 20 5.33333 20H16.6667C17.5871 20 18.3333 19.2538 18.3333 18.3333V14.6667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <p className="text-[14px] text-[#41c291]">
                          {additionalInfo.attachment
                            ? additionalInfo.attachment.name
                            : "Drag & Drop"}
                        </p>
                      </div>
                    </div>
                    {additionalInfo.attachment && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAdditionalInfo({
                            ...additionalInfo,
                            attachment: null,
                          });
                        }}
                        className="mt-2 text-[12px] text-red-500 hover:text-red-700"
                      >
                        Remove file
                      </button>
                    )}
                  </div>

                  <button
                    onClick={handleSendAdditionalInfoRequest}
                    disabled={
                      sendingRequest ||
                      uploadingFile ||
                      !additionalInfo.message.trim()
                    }
                    className="w-full px-[24px] py-[2px] h-[40px] bg-gradient-to-r from-[#40C191] to-[#5ED58D] text-white rounded-[8px] text-[14px] font-medium hover:from-[#35A47C] hover:to-[#4EC57A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingRequest
                      ? "Sending..."
                      : uploadingFile
                        ? "Uploading..."
                        : "Send Request"}
                  </button>
                </div>
              </div>

              {/* Fees */}
              <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
                <h3 className="text-[18px] font-normal text-[#222f2a] mb-[16px] pb-[8px]">
                  Fees
                </h3>

                <div className="space-y-0">
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Copyright licence fee:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.fee}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Translation fee × {translationCount}:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.translationFee}
                    </p>
                  </div>
                  <div className="h-px bg-[#ebebeb]"></div>

                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                      Support documents:
                    </p>
                    <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                      {formattedData.supportFee}
                    </p>
                  </div>
                  {licenceData.fees?.otherFeesTotal > 0 && (
                    <>
                      <div className="h-px bg-[#ebebeb]"></div>
                      <div className="flex gap-[16px] items-start py-[12px]">
                        <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                          Other fees:
                        </p>
                        <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                          £{(licenceData.fees?.otherFeesTotal || 0).toFixed(2)}
                        </p>
                      </div>
                    </>
                  )}
                  {licenceData.fees?.ecoaFeeTotal > 0 && (
                    <>
                      <div className="h-px bg-[#ebebeb]"></div>
                      <div className="flex gap-[16px] items-start py-[12px]">
                        <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                          eCOA fees:
                        </p>
                        <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                          {formattedData.ecoaFee}
                        </p>
                      </div>
                    </>
                  )}
                  <div className="h-px bg-[#ebebeb]"></div>

                  {licenceData.fees?.vatApplicable &&
                  (licenceData.fees?.vatAmount || 0) > 0 ? (
                    <>
                      <div className="flex gap-[16px] items-start py-[12px]">
                        <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                          Subtotal (ex. VAT):
                        </p>
                        <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                          {formattedData.totalPrice}
                        </p>
                      </div>
                      <div className="h-px bg-[#ebebeb]"></div>
                      <div className="flex gap-[16px] items-start py-[12px]">
                        <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                          VAT (
                          {Math.round((licenceData.fees?.vatRate || 0.2) * 100)}
                          %):
                        </p>
                        <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                          £{(licenceData.fees?.vatAmount || 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="h-px bg-[#ebebeb]"></div>
                      <div className="flex gap-[16px] items-start py-[12px]">
                        <p className="flex-1 font-bold text-[16px] text-[#222f2a]">
                          Total (inc. VAT):
                        </p>
                        <p className="flex-1 font-bold text-[16px] text-[#222f2a] text-right">
                          £
                          {(
                            licenceData.fees?.grandTotal ??
                            licenceData.fees?.totalPrice ??
                            0
                          ).toFixed(2)}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex gap-[16px] items-start py-[12px]">
                      <p className="flex-1 font-bold text-[16px] text-[#222f2a]">
                        Total price:
                      </p>
                      <p className="flex-1 font-bold text-[16px] text-[#222f2a] text-right">
                        {formattedData.totalPrice}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment */}
              <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
                <h3 className="text-[18px] font-normal text-[#222f2a] mb-[16px] pb-[8px]">
                  Payment
                </h3>

                <div className="flex gap-[16px] items-center py-[12px]">
                  <p className="font-medium text-[16px] text-[#485b54]">
                    Status:
                  </p>
                  <div className="flex-1 relative">
                    <select
                      value={formattedData.paymentStatus || "NotRequired"}
                      onChange={handlePaymentStatusChange}
                      disabled={updatingPayment || showExpirationModal || (formattedData.paymentStatus === "Refunded")}
                      className="w-full px-[12px] py-[8px] bg-white border border-[#ebebeb] rounded-[8px] text-[14px] text-[#73827c] appearance-none focus:outline-none focus:ring-2 focus:ring-[#40C191] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="NotRequired">Not Required</option>
                      <option value="Pending">Payment Pending</option>
                      <option value="Completed">Payment Completed</option>
                      <option value="Failed">Payment Failed</option>
                      <option value="Refunded">Refunded</option>
                    </select>
                    <div className="absolute right-[12px] top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="#73827c"
                        strokeWidth="1.5"
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Expiration Date Display/Edit — always visible */}
                <div className="flex gap-[16px] items-center py-[12px] border-t border-[#ebebeb] mt-[12px]">
                  <p className="font-medium text-[16px] text-[#485b54]">
                    Expiration Date:
                  </p>
                  <div className="flex-1">
                    {licenceData?.expiration?.expiresAt ? (
                      <>
                        <p className="text-[14px] text-[#73827c]">
                          {new Date(
                            licenceData.expiration.expiresAt,
                          ).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </p>
                        <button
                          onClick={() => {
                            setExpirationDate(
                              new Date(licenceData.expiration.expiresAt)
                                .toISOString()
                                .split("T")[0],
                            );
                            setPendingPaymentStatus(null); // Clear any pending payment status
                            setOriginalPaymentStatus(null);
                            setShowExpirationModal(true);
                          }}
                          className="mt-[4px] text-[12px] text-[#40C191] hover:underline"
                        >
                          Change Expiration Date
                        </button>
                      </>
                    ) : (
                      <div>
                        <p className="text-[14px] text-[#a0a0a0] italic">
                          Not set
                        </p>
                        <button
                          onClick={() => {
                            // Set default expiration to 30 days from now
                            const defaultDate = new Date();
                            defaultDate.setDate(defaultDate.getDate() + 30);
                            setExpirationDate(defaultDate.toISOString().split("T")[0]);
                            setPendingPaymentStatus(null); // Not changing payment, just expiration
                            setOriginalPaymentStatus(null);
                            setShowExpirationModal(true);
                          }}
                          className="mt-[4px] text-[12px] text-[#40C191] hover:underline"
                        >
                          Set Expiration Date
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Invoice Section */}
                {licenceData?.status === "Approved" && (
                  <div className="border-t border-[#ebebeb] mt-[12px] pt-[12px]">
                    <p className="font-medium text-[16px] text-[#485b54] mb-[8px]">
                      Invoice
                    </p>
                    {invoice ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[14px] text-[#73827c]">
                            Invoice Number:
                          </span>
                          <button
                            onClick={() => navigate(`/invoices/${invoice._id}`)}
                            className="text-[14px] text-[#40C191] hover:underline font-medium"
                          >
                            {invoice.invoiceNumber}
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[14px] text-[#73827c]">
                            Status:
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              invoice.status === "PAID"
                                ? "bg-green-100 text-green-700"
                                : invoice.status === "AUTHORISED"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {invoice.status}
                          </span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={async () => {
                              try {
                                const blob = await getInvoicePDF(invoice._id);
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement("a");
                                link.href = url;
                                link.download = `invoice-${invoice.invoiceNumber}.pdf`;
                                link.click();
                                window.URL.revokeObjectURL(url);
                              } catch (err) {
                                toast.error("Failed to download invoice PDF");
                              }
                            }}
                            className="flex-1 px-3 py-2 text-[12px] font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                          >
                            Download PDF
                          </button>
                          {invoice.status !== "PAID" && invoice.status !== "REFUNDED" && invoice.status !== "VOIDED" ? (
                            <button
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    "Send invoice to customer via email?",
                                  )
                                )
                                  return;
                                try {
                                  setSendingInvoice(true);
                                  await sendInvoice(invoice._id);
                                  toast.success("Invoice sent successfully!");
                                  await loadData();
                                } catch (err) {
                                  toast.error(
                                    "Failed to send invoice: " +
                                      (err.message || "Unknown error"),
                                  );
                                } finally {
                                  setSendingInvoice(false);
                                }
                              }}
                              disabled={sendingInvoice}
                              className="flex-1 px-3 py-2 text-[12px] font-medium text-white bg-emerald-400 rounded-lg hover:bg-emerald-500 disabled:opacity-50"
                            >
                              {sendingInvoice ? "Sending..." : "Send Invoice"}
                            </button>
                          ) : (
                            <button
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    "Generate a new invoice for this licence?",
                                  )
                                )
                                  return;
                                try {
                                  setGeneratingInvoice(true);
                                  const response = await generateInvoice(id, 'licence');
                                  if (response && response.success !== false) {
                                    setInvoice(response.data || response);
                                    toast.success("Invoice generated successfully!");
                                    await loadData();
                                  } else {
                                    toast.error(response?.error || "Failed to generate invoice");
                                  }
                                } catch (err) {
                                  toast.error(
                                    "Failed to generate invoice: " +
                                      (err.message || "Unknown error"),
                                  );
                                } finally {
                                  setGeneratingInvoice(false);
                                }
                              }}
                              disabled={generatingInvoice}
                              className="flex-1 px-3 py-2 text-[12px] font-medium text-white bg-emerald-400 rounded-lg hover:bg-emerald-500 disabled:opacity-50"
                            >
                              {generatingInvoice ? "Generating..." : "Generate New Invoice"}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={async () => {
                          if (
                            !window.confirm(
                              "Generate invoice for this licence?",
                            )
                          )
                            return;
                          try {
                            setGeneratingInvoice(true);
                            const response = await generateInvoice(id);
                            if (response.success) {
                              setInvoice(response.data);
                              toast.success("Invoice generated successfully!");
                              await loadData();
                            }
                          } catch (err) {
                            toast.error(
                              "Failed to generate invoice: " +
                                (err.message || "Unknown error"),
                            );
                          } finally {
                            setGeneratingInvoice(false);
                          }
                        }}
                        disabled={generatingInvoice}
                        className="w-full px-4 py-2 text-[14px] font-medium text-white bg-emerald-400 rounded-lg hover:bg-emerald-500 disabled:opacity-50"
                      >
                        {generatingInvoice
                          ? "Generating..."
                          : "Generate Invoice"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          ) : (
          <div className="bg-white border border-[#f2f2f2] rounded-[16px] p-6">
            <TranslationRequestsTab
              licenceId={id}
              isEditable={true}
              onAwaitingCountChange={setNewTranslationRequestCount}
              onTotalCountChange={setTranslationRequestTotalCount}
            />
          </div>
          )}
        </div>

        {/* Message Modal - Outside main layout */}
        {showMessageModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowMessageModal(false)}
          >
            <div
              className="bg-white rounded-[12px] w-[90vw] h-[90vh] max-w-[1200px] flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-[#f2f2f2]">
                <h2 className="text-[20px] font-semibold text-[#222f2a]">
                  Messages
                </h2>
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              {/* Tabs */}
              <div className="bg-[#f2f2f2] p-[6px] border-b border-[#f2f2f2]">
                <div className="flex gap-0">
                  {["applicants", "staff"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveChatTab(tab)}
                      className={`flex-1 px-[16px] py-[6px] text-[14px] font-medium rounded-[8px] transition-colors ${
                        activeChatTab === tab
                          ? "bg-emerald-400 text-white"
                          : "text-emerald-400 hover:bg-white"
                      }`}
                    >
                      {tab === "applicants" ? "Applicants" : "Staff"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Messages Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {(() => {
                  // Combine and sort all entries by date
                  const allEntries = [
                    ...timeline.map((entry) => ({
                      type: "timeline",
                      data: entry,
                      date: entry.at ? new Date(entry.at) : new Date(0),
                    })),
                    ...messages.map((msg) => ({
                      type: "message",
                      data: msg,
                      date: msg.createdAt
                        ? new Date(msg.createdAt)
                        : new Date(0),
                    })),
                  ].sort((a, b) => a.date - b.date);

                  if (allEntries.length === 0) {
                    return (
                      <p className="text-sm text-gray-500 text-center py-8">
                        No messages yet
                      </p>
                    );
                  }

                  return allEntries.map((item, index) => {
                    const isLast = index === allEntries.length - 1;

                    if (item.type === "timeline") {
                      const entry = item.data;
                      const isCompleted =
                        entry.type !== "pending" && entry.type !== "due_date";
                      const date = entry.at ? new Date(entry.at) : null;
                      const formattedDate = date
                        ? date.toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "";
                      const formattedTime = date
                        ? date.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "";

                      return (
                        <div
                          key={`timeline-${entry._id || index}`}
                          className="flex gap-3"
                        >
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div
                              className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                isCompleted
                                  ? "bg-emerald-400"
                                  : "bg-white border-2 border-gray-300"
                              }`}
                            >
                              {isCompleted ? (
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                >
                                  <path
                                    d="M3.5 7L5.83333 9.33333L10.5 4.66667"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              ) : (
                                <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                              )}
                            </div>
                            {!isLast && (
                              <div className="w-0.5 flex-1 bg-gray-200 mt-2 mb-2 min-h-[20px]"></div>
                            )}
                          </div>
                          <div className="flex-1 pb-4">
                            <div className="flex items-center justify-between mb-1">
                              <span
                                className={`text-xs font-medium ${
                                  isCompleted
                                    ? "text-emerald-600"
                                    : "text-gray-900"
                                }`}
                              >
                                {entry.title || entry.type}
                              </span>
                              {formattedTime && (
                                <span className="text-xs text-gray-500">
                                  {formattedTime}
                                </span>
                              )}
                            </div>
                            {formattedDate && (
                              <p className="text-xs text-gray-500 mb-3">
                                {formattedDate}
                              </p>
                            )}
                            {entry.body && (
                              <div
                                className={`rounded-lg p-3 ${
                                  entry.type === "message" ||
                                  entry.type === "request"
                                    ? "bg-purple-50 border border-purple-200"
                                    : "bg-emerald-50 border border-emerald-200"
                                }`}
                              >
                                <p className="text-xs text-gray-700 mb-2">
                                  {entry.body}
                                </p>
                                {entry.meta &&
                                  Object.keys(entry.meta).length > 0 && (
                                    <div className="space-y-1 text-xs">
                                      {entry.meta.quoteType && (
                                        <>
                                          <p className="text-emerald-600">
                                            Quote Type
                                          </p>
                                          <p className="text-gray-700">
                                            {entry.meta.quoteType}
                                          </p>
                                        </>
                                      )}
                                      {entry.meta.amount && (
                                        <>
                                          <p className="text-xs font-semibold text-gray-900 mb-1">
                                            Amount:
                                          </p>
                                          <p className="text-xs font-bold text-gray-900">
                                            £{entry.meta.amount}
                                          </p>
                                        </>
                                      )}
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    } else {
                      // Message entry
                      const msg = item.data;
                      const date = msg.createdAt
                        ? new Date(msg.createdAt)
                        : null;
                      const formattedDate = date
                        ? date.toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "";
                      const formattedTime = date
                        ? date.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "";
                      const isFromCustomer =
                        !!msg.fromCustomerId && !msg.fromUserId;
                      const isFromAdmin =
                        !!msg.fromUserId && !msg.fromCustomerId;

                      if (isFromCustomer) {
                        const customerName =
                          typeof msg.fromCustomerId === "object"
                            ? `${msg.fromCustomerId.firstName || ""} ${msg.fromCustomerId.lastName || ""}`.trim() ||
                              "Customer"
                            : "Customer";

                        return (
                          <div
                            key={`msg-${msg._id || index}`}
                            className="flex items-start gap-3 mb-6"
                          >
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-medium text-gray-600">
                                {customerName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {customerName}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {formattedTime}
                                </span>
                              </div>
                              {formattedDate && (
                                <p className="text-sm text-gray-500 mb-2">
                                  {formattedDate}
                                </p>
                              )}
                              {msg.messageType === "licence_request" ||
                              msg.meta?.type === "license" ? (
                                <div className="bg-white border border-gray-200 rounded-lg p-5 mt-2 shadow-sm max-w-[600px]">
                                  <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        stroke="#73827C"
                                        strokeWidth="1.5"
                                      >
                                        <path
                                          d="M13.3333 4.66667H2.66667C2.29848 4.66667 2 4.96515 2 5.33333V12C2 12.3682 2.29848 12.6667 2.66667 12.6667H13.3333C13.7015 12.6667 14 12.3682 14 12V5.33333C14 4.96515 13.7015 4.66667 13.3333 4.66667Z"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M10.6667 4.66667V3.33333C10.6667 3.06812 10.5524 2.81377 10.3492 2.62373C10.1461 2.43369 9.87681 2.33333 9.6 2.33333H6.4C6.12319 2.33333 5.85386 2.43369 5.65074 2.62373C5.44762 2.81377 5.33333 3.06812 5.33333 3.33333V4.66667"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                      <span className="text-sm font-semibold text-gray-900">
                                        Licence Request
                                      </span>
                                    </div>
                                    {msg.meta?.dateRange && (
                                      <span className="text-sm text-gray-500">
                                        {msg.meta.dateRange}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                                    {msg.meta?.message || msg.body}
                                  </p>
                                  {msg.meta?.organizationType && (
                                    <div className="mb-3">
                                      <div className="flex items-start gap-2">
                                        <span className="text-sm text-gray-600 font-medium">
                                          Organisation Type:
                                        </span>
                                        <span className="text-sm text-gray-900 flex-1">
                                          {msg.meta.organizationType}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  {msg.meta?.translations &&
                                    msg.meta.translations.length > 0 && (
                                      <div className="mb-4">
                                        <div className="flex items-start gap-2 mb-2">
                                          <span className="text-sm text-gray-600 font-medium">
                                            Translations:
                                          </span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {msg.meta.translations.map(
                                            (lang, idx) => (
                                              <span
                                                key={idx}
                                                className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-md border border-gray-200"
                                              >
                                                {lang}
                                              </span>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  <button
                                    onClick={() => {
                                      window.scrollTo({
                                        top: 0,
                                        behavior: "smooth",
                                      });
                                      setShowMessageModal(false);
                                    }}
                                    className="text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors mt-2"
                                  >
                                    View Details
                                  </button>
                                </div>
                              ) : (
                                msg.body && (
                                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm text-gray-700">
                                      {msg.body}
                                    </p>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        );
                      } else if (isFromAdmin) {
                        const adminName =
                          typeof msg.fromUserId === "object"
                            ? `${msg.fromUserId.firstName || ""} ${msg.fromUserId.lastName || ""}`.trim() ||
                              "Admin"
                            : "Admin";

                        return (
                          <div
                            key={`msg-${msg._id || index}`}
                            className="flex flex-col items-end gap-2 mb-6"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-500">
                                {formattedTime}
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {adminName}
                              </span>
                            </div>
                            {formattedDate && (
                              <p className="text-sm text-gray-500">
                                {formattedDate}
                              </p>
                            )}
                            {msg.body && (
                              <div className="bg-emerald-500 rounded-lg p-4 max-w-[80%]">
                                <p className="text-sm text-white">{msg.body}</p>
                              </div>
                            )}
                          </div>
                        );
                      }

                      return (
                        <div
                          key={`msg-${msg._id || index}`}
                          className="flex gap-3 mb-4"
                        >
                          <div className="flex-1">
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <p className="text-sm text-gray-700">
                                {msg.body}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  });
                })()}
              </div>

              {/* Message Input */}
              <div className="border-t border-[#e7ebe9] p-4">
                <div className="flex gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Type your message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    // onKeyPress={(e) => {
                    //   if (e.key === "Enter" && !e.shiftKey) {
                    //     e.preventDefault();
                    //     handleSendMessage();
                    //   }
                    // }}
                    disabled={sendingMessage}
                    className="flex-1 px-4 py-3 bg-white border border-[#ebebeb] rounded-[8px] text-[14px] text-[#73827c] focus:outline-none focus:ring-2 focus:ring-[#40C191] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || !messageText.trim()}
                    className="px-6 py-3 bg-[#41c291] rounded-[8px] hover:bg-[#35A47C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium"
                  >
                    {sendingMessage ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </div>
                    ) : (
                      "Send"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send Back to Reviewer Modal */}
        <SendBackToReviewerModal
          isOpen={showSendBackModal}
          onClose={() => setShowSendBackModal(false)}
          onConfirm={handleSendBackToReviewer}
          licenceRefNumber={licenceData?.refNumber || ""}
        />

        {/* Expiration Date Modal */}
        {showExpirationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-[12px] p-[24px] max-w-md w-full mx-4">
              <h3 className="text-[20px] font-semibold text-[#222f2a] mb-[16px]">
                Set Licence Expiration Date
              </h3>
              <p className="text-[14px] text-[#73827c] mb-[16px]">
                Please select the expiration date for this licence. The licence
                will expire on the selected date.
              </p>
              <div className="mb-[16px]">
                <label className="block text-[14px] font-medium text-[#485b54] mb-[8px]">
                  Expiration Date
                </label>
                <input
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-[12px] py-[8px] bg-white border border-[#ebebeb] rounded-[8px] text-[14px] text-[#73827c] focus:outline-none focus:ring-2 focus:ring-[#40C191]"
                />
              </div>
              <div className="flex gap-[12px] justify-end">
                <button
                  onClick={() => {
                    setShowExpirationModal(false);
                    setPendingPaymentStatus(null);
                    setExpirationDate("");
                    setOriginalPaymentStatus(null);
                  }}
                  className="px-[16px] py-[8px] border border-[#ebebeb] rounded-[8px] text-[14px] text-[#73827c] hover:bg-[#F8F9FA] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmExpiration}
                  disabled={updatingPayment || !expirationDate}
                  className="px-[16px] py-[8px] bg-[#40C191] text-white rounded-[8px] text-[14px] font-medium hover:bg-[#35A47B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updatingPayment ? "Saving..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Document Viewer Modal */}
        {selectedDocument && (
          <DocumentViewer
            isOpen={showDocumentViewer}
            onClose={() => {
              setShowDocumentViewer(false);
              setSelectedDocument(null);
            }}
            document={selectedDocument}
          />
        )}

        {/* Support document upload modal */}
        <AddSupportDocumentModal
          isOpen={isAddDocumentModalOpen}
          onClose={() => setIsAddDocumentModalOpen(false)}
          onAdd={handleAddSupportDocument}
        />
      </div>
    </>
  );
};

export default LicenceDetails;
